<template>
    <div>
        <!-- CALENDAR -->
        <div class="calendar-container">
            <month-calendar v-model="calendar" :events="daysWithRoutines" multiple :month.sync="pickerDate" />
        </div>
        <div class="cal-divider" />

        <v-row>
            <v-col cols="12">
                <kimsa-combobox :placeholder="$t('inputs.students_placeholder')" dense solo :items="students"
                    @change="setCurrentStudent" :value="currentStudent" item-value="objectID" clearable searchable
                    icon="$account" />
            </v-col>
        </v-row>

        <v-row v-if="loading">
            <v-col cols="12">
                <skeleton-loader type="lib-rows" :count="4" />
            </v-col>
        </v-row>
        <v-row v-else>
            <!-- ROUTINES -->
            <v-col cols="12" v-if="filteredRoutines.length > 0">
                <v-expansion-panels accordion flat v-model="openFirst" class="day-groups">
                    <v-expansion-panel v-for="(date, i) in filteredRoutines" :key="i" class="day-panel">
                        <v-expansion-panel-header class="day-hdr" ripple>
                            <div class="day-hdr-left">
                                <span class="day-hdr-date">{{ date.name }}</span>
                                <span class="day-hdr-count">{{ date.routines.length }}</span>
                            </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content class="day-body">
                            <div v-if="openFirst === i">
                                <v-virtual-scroll :items="date.sorted" item-height="90" max-height="500" :bench="8"
                                    :key="`vs-${i}-${date.date}-${refreshKey}`">
                                    <template #default="{ item }">
                                        <div class="day-card-wrap">
                                            <routine-card :student-name="getStudentName(item.studentId)" :routine="item"
                                                @click="goToAssignedRoutine(item)" withMenu emitCopy noAssign noFolder
                                                emit-edit-assigned @click:copy="showCopyRoutine(item)"
                                                @click:delete="showDeleteRoutine(item)"
                                                @click:edit:assigned="showEditAssigned(item)"
                                                @click:edit:competence="showEditCompetence(item)" />
                                        </div>
                                    </template>
                                </v-virtual-scroll>
                            </div>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
            </v-col>

            <!-- NO ROUTINES -->
            <v-col cols="12" v-if="showNoRoutines && currentStudent" class="text-center py-8">
                <v-icon size="48" color="txtMuted">mdi-calendar-blank-outline</v-icon>
                <div class="empty-text my-3">
                    {{ $t('admin.no_assigned_routines') }}
                </div>
                <v-btn color="primary" class="black--text normal-btn" depressed @click="goToRoutines">
                    {{ $t('coach.see_routines') }}
                </v-btn>
            </v-col>

            <copy-routine-dialog :coach-id="coachId" @success="loadRoutines" :students-options="students"
                :groups-options="groups" :teams-options="teams" :current-selection="currentSelection" ref="copy"
                auto-date />

            <add-competence-dialog :current-selection="currentSelection" collection="assignedRoutines"
                :coach-id="coachId" ref="competence" @saved="onSaveCompetence" :routine-number="routineNumber" />

            <kimsa-confirm :title="$t('routines.delete')" :content="$t('routines.confirm_delete')"
                @confirm="onDeleteRoutine" :close-on-confirm="false" :loading="loadingDelete" alert-mode
                ref="confirm" />

            <routine-editor ref="editor" :coach-id="coachId" @success="loadRoutines" />

            <note-reminder-dialog />
        </v-row>
    </div>
</template>

<script>

import { mapState, mapActions, mapMutations } from 'vuex'
import RoutineCard from '@/components/common/routines/RoutineCard'
import _ from "lodash";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import MonthCalendar from "@/components/common/MonthCalendar";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import { UserIndex } from "@/utils/Algolia";
import { uniqueDayRoutine } from '../../../utils/Exercises';
import CopyRoutineDialog from '../../common/routines/CopyRoutineDialog.vue';
import AddCompetenceDialog from '../competences/AddCompetenceDialog.vue';
import KimsaConfirm from '../../common/KimsaConfirm.vue';
import RoutineEditor from './RoutineEditor.vue';
import NoteReminderDialog from '../NoteReminderDialog.vue';
import { db, getAllByIds } from "@/utils/Firebase";

export default {
    name: "CalendarMobile",
    components: {
        KimsaCombobox,
        SkeletonLoader,
        MonthCalendar,
        RoutineCard,
        CopyRoutineDialog,
        AddCompetenceDialog,
        KimsaConfirm,
        RoutineEditor,
        NoteReminderDialog,
    },
    props: {
        defaultDate: {
            type: String,
            default: null
        },
    },
    data() {
        const initialDate = this.defaultDate || this.$route.query.date || null
        return {
            loading: false,
            openFirst: 0,
            calendar: initialDate ? [initialDate] : [],
            pickerDate: initialDate ? this.$moment(initialDate).format('YYYY-MM') : null,
            routines: [],
            students: [],
            currentSelection: null,
            currentRoutine: null,
            loadingDelete: false,
            currentStudent: null,
            refreshKey: 0
        }
    },
    computed: {
        ...mapState(['coachAssignedRoutines', 'groups', 'teams', 'libraryCoach']),
        filteredRoutines() {
            if (this.calendar.length === 0) return this.sortedByDate;
            return this.sortedByDate.filter(d => this.calendar.includes(d.date));
        },
        sortedByDate() {
            return this.routines.map(d => ({
                ...d,
                sorted: [...d.routines].sort((a, b) => {
                    const nameA = a?.name?.toLowerCase() || "";
                    const nameB = b?.name?.toLowerCase() || "";
                    return nameA.localeCompare(nameB);
                })
            }));
        },
        noRoutines(vm = this) {
            return vm.coachAssignedRoutines.length === 0
        },
        coachId(vm = this) {
            return vm.isCompany ?
                vm.libraryCoach?.id || null :
                vm.currentUser?.uid || null
        },
        showNoRoutines(vm = this) {
            return !!vm.coachId && vm.noRoutines
        },
        routineNumber(vm = this) {
            return vm.currentRoutine?.routineNumber || 1
        },
        routineDates(vm = this) {
            return new Set(vm.routines.map(d => d.date))
        },
    },
    async mounted() {
        const vm = this
        await vm.loadStudents()
        await vm.loadRoutines()
        vm.groupRoutines()
    },
    methods: {
        ...mapMutations(['setShowCalendar', 'setLibraryTab']),
        ...mapActions(['fetchCoachAssignedRoutines']),
        goToRoutines() {
            const vm = this
            vm.setLibraryTab('routines')
            vm.$router.push({ name: `${vm.accountType}.library` })
        },
        async loadStudents() {
            const vm = this
            if (!vm.coachId) { vm.students = []; return }
            let filter = `studentSubscription.coachId: ${vm.coachId} AND studentSubscription.paused != 1`
            await UserIndex.search('', {
                filters: filter,
                hitsPerPage: 1000,
            })
                .then(({ hits }) => {
                    vm.students = hits.map(hit => ({ ...hit, name: hit.name || hit.email }))
                    const studentId = vm.$route.query.studentId
                    if (studentId) {
                        vm.currentStudent = vm.students.find(student => student.objectID === studentId)
                    }
                    if (studentId || vm.$route.query.date) {
                        const { studentId: _s, date: _d, ...query } = vm.$route.query
                        vm.$router.replace({ query })
                    }
                })
                .catch(() => { });
        },
        getStudentName(studentId) {
            const vm = this
            let student = vm.students.find(student => student.objectID === studentId)
            return student?.name || '...'
        },
        async loadRoutines(date = null) {
            const vm = this
            await vm.withLoading(async () => {
                let payload = { coachId: vm.coachId }
                let baseDate = date || vm.pickerDate
                payload.date = baseDate
                    ? vm.$moment(baseDate).startOf('month').format('YYYY-MM-DD')
                    : vm.$moment().startOf('month').format('YYYY-MM-DD')
                payload.studentId = vm.currentStudent?.objectID
                await vm.fetchCoachAssignedRoutines(payload)
            })
        },
        async ensureStudent(studentId) {
            const vm = this
            if (!studentId) return null
            let student = vm.students.find(s => s.objectID === studentId)
            if (student) return student
            const [doc] = await getAllByIds('users', [studentId])
            if (!doc) return null
            student = { ...doc, name: doc.name || doc.email }
            vm.students = [...vm.students, student]
            return student
        },
        daysWithRoutines(date) {
            return this.routineDates.has(date)
        },
        goToAssignedRoutine(routine) {
            const vm = this
            vm.$router.push({ name: `${vm.accountType}.calendar.assignedRoutine`, params: { uid: routine.id } })
        },
        showEditAssigned(routine) {
            const vm = this
            vm.$refs.editor.showEditAssigned(routine)
        },
        groupRoutines() {
            const vm = this
            let routines = _.groupBy(uniqueDayRoutine(vm.coachAssignedRoutines).map(routine => {
                return {
                    ...routine,
                    id: routine.id,
                    date: routine.rawDate.full,
                    dateString: vm.$moment(routine.rawDate.full).format(vm.$t('common.date_format_day_month'))
                }
            }), 'dateString')

            vm.routines = Object.entries(routines).map(routine => {
                return { name: routine[0], date: routine[1][0].date, routines: routine[1] }
            }).sort((a, b) => vm.$moment(a.date).valueOf() - vm.$moment(b.date).valueOf());
        },
        findByName(list, value) {
            if (value?.name) return value
            if (typeof value !== 'string') return null
            const term = value.toLowerCase()
            return list?.find(item => item.name?.toLowerCase() === term) || null
        },
        setCurrentStudent(student) {
            const vm = this
            vm.currentStudent = vm.findByName(vm.students, student)
        },
        async showCopyRoutine(routine) {
            const vm = this;
            vm.currentSelection = await vm.ensureStudent(routine.studentId)
            vm.$refs.copy.show({ date: routine.rawDate.full, routineId: routine.routineId, assignedRoutineId: routine.id })
        },
        async showEditCompetence(routine) {
            const vm = this;
            if (!vm.requirePlan('competences')) return
            vm.currentRoutine = routine
            vm.currentSelection = await vm.ensureStudent(routine.studentId)
            vm.$refs.competence.show(routine.rawDate.full, routine)
        },
        onSaveCompetence() {
            const vm = this;
            vm.currentSelection = null
            vm.currentRoutine = null
        },
        showDeleteRoutine(routine) {
            const vm = this
            let assignedRoutineId = routine?.id || null
            if (!assignedRoutineId) return vm.toast(vm.$t('routines.error_with_routine'), 'error')
            vm.currentRoutine = routine
            vm.$refs.confirm.show()
        },
        async onDeleteRoutine() {
            const vm = this
            await vm.withLoading(async () => {
                const deletedId = vm.currentRoutine.id
                await db.collection('assignedRoutines').doc(deletedId).delete()
                vm.$store.commit('setCoachAssignedRoutines', vm.coachAssignedRoutines.filter(r => r.id !== deletedId))
                vm.$refs.confirm?.hide()
                vm.currentRoutine = null
                vm.toast(vm.$t('routines.deleted'))
            }, 'loadingDelete')
        },
    },
    watch: {
        async coachId() {
            const vm = this
            await vm.loadStudents()
            if (vm.currentStudent) vm.currentStudent = null
            else await vm.loadRoutines()
        },
        async currentStudent() {
            const vm = this
            await vm.loadRoutines()
        },
        coachAssignedRoutines() {
            this.groupRoutines()
        },
        async pickerDate(val) {
            const vm = this
            await vm.loadRoutines(val)
            if (!vm.defaultDate) vm.calendar = []
        },
        defaultDate(val) {
            if (val) this.calendar = [val]
        },
        filteredRoutines() {
            this.refreshKey++
        },
        openFirst() {
            this.$nextTick(() => {
                this.refreshKey++
            })
        }
    },
    beforeRouteLeave(to, from, next) {
        const vm = this
        vm.setShowCalendar(false)
        next()
    }
}
</script>

<style scoped>
.calendar-container {
    margin: -12px -12px 0;
    background: var(--v-surface-base);
    border-top: 1px solid var(--v-outline-base);
}

.cal-divider {
    height: 2px;
    border-radius: 2px;
    margin-bottom: 12px;
    background: linear-gradient(90deg, var(--v-primary-base), var(--v-success-base));
}

.empty-text {
    font-size: 14px;
    color: var(--v-txtSecondary-base);
}

/* ── Day groups (accordion) ── */
.day-groups {
    background: transparent;
}

.day-groups ::v-deep>.v-expansion-panel {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 12px !important;
    margin-bottom: 8px;
    overflow: hidden;
}

.day-groups ::v-deep>.v-expansion-panel::before {
    box-shadow: none !important;
}

.day-groups ::v-deep>.v-expansion-panel::after {
    display: none;
}

.day-hdr {
    min-height: 52px;
    padding: 0 16px;
}

.day-hdr-left {
    display: flex;
    align-items: center;
    gap: 8px;
}

.day-hdr-date {
    font-size: 15px;
    font-weight: 700;
    color: var(--v-secondary-base);
}

.theme--dark .day-hdr-date {
    color: var(--v-primary-base);
}

.day-hdr-count {
    font-size: 12px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
    background: var(--v-surface2-base);
    border-radius: 20px;
    padding: 1px 8px;
}

.day-groups ::v-deep .v-expansion-panel-header__icon .v-icon {
    color: var(--v-txtSecondary-base);
}

.day-body ::v-deep .v-expansion-panel-content__wrap {
    padding: 0 8px 8px;
}

.day-card-wrap {
    padding-bottom: 8px;
}

.day-card-wrap:last-child {
    padding-bottom: 0;
}
</style>